import type { AssignedSeatingMap, SeatHold } from "./types";
import { removeUnavailableAssignedSeats } from "./selection";

export const getSeatHoldRemainingSeconds = (
  expiresAt?: string,
  now = Date.now()
) => {
  const expiryTime = expiresAt ? Date.parse(expiresAt) : Number.NaN;
  return Number.isFinite(expiryTime)
    ? Math.max(0, Math.ceil((expiryTime - now) / 1000))
    : 0;
};

export const buildSeatHoldPayload = ({
  seatMap,
  selectedSeatKeys,
}: {
  seatMap?: AssignedSeatingMap | null;
  selectedSeatKeys: string[];
}) => ({
  seatKeys: removeUnavailableAssignedSeats({ seatMap, selectedSeatKeys }),
});

export const parseSeatHold = (value: unknown): SeatHold | null => {
  if (typeof value !== "object" || value === null) {
    return null;
  }

  const hold = value as Record<string, unknown>;
  const seatKeys = hold.seatKeys;
  const expiresAt = hold.expiresAt;

  if (
    typeof hold.holdId !== "string" ||
    !hold.holdId ||
    !Array.isArray(seatKeys) ||
    !seatKeys.length ||
    !seatKeys.every((seatKey) => typeof seatKey === "string" && seatKey) ||
    typeof expiresAt !== "string" ||
    Number.isNaN(Date.parse(expiresAt))
  ) {
    return null;
  }

  return { holdId: hold.holdId, seatKeys, expiresAt };
};

interface SeatHoldSynchronizationDependencies {
  // eslint-disable-next-line no-unused-vars
  createSeatHold: (seatKeys: string[]) => Promise<unknown>;
  // eslint-disable-next-line no-unused-vars
  updateSeatHold: (holdId: string, seatKeys: string[]) => Promise<unknown>;
  // eslint-disable-next-line no-unused-vars
  releaseSeatHold: (holdId: string) => Promise<unknown>;
}

export const synchronizeSeatHoldSelection = async ({
  seatHold,
  seatKeys,
  createSeatHold,
  updateSeatHold,
  releaseSeatHold,
}: SeatHoldSynchronizationDependencies & {
  seatHold: SeatHold | null;
  seatKeys: string[];
}): Promise<SeatHold | null> => {
  if (!seatKeys.length) {
    if (seatHold) {
      await releaseSeatHold(seatHold.holdId);
    }
    return null;
  }

  const response = seatHold
    ? await updateSeatHold(seatHold.holdId, seatKeys)
    : await createSeatHold(seatKeys);

  return parseSeatHold(response);
};

export interface SeatHoldSelectionState {
  seatHold: SeatHold | null;
  selectedSeatKeys: string[];
}

export const createSeatHoldSelectionCoordinator = () => {
  let state: SeatHoldSelectionState = {
    seatHold: null,
    selectedSeatKeys: [],
  };

  const getState = () => state;

  return {
    getState,
    setState: (nextState: SeatHoldSelectionState) => {
      state = nextState;
    },
    synchronize: async ({
      seatKeys,
      createSeatHold,
      updateSeatHold,
      releaseSeatHold,
    }: SeatHoldSynchronizationDependencies & { seatKeys: string[] }) => {
      const nextSeatHold = await synchronizeSeatHoldSelection({
        seatHold: state.seatHold,
        seatKeys,
        createSeatHold,
        updateSeatHold,
        releaseSeatHold,
      });

      if (seatKeys.length && !nextSeatHold) {
        return getState();
      }

      state = {
        seatHold: nextSeatHold,
        selectedSeatKeys: nextSeatHold ? nextSeatHold.seatKeys : [],
      };
      return getState();
    },
  };
};

export const isSeatHoldExpired = (hold: SeatHold, now = Date.now()) =>
  Date.parse(hold.expiresAt) <= now;

export const loadCurrentSeatHold = async (
  getCurrentSeatHold: () => Promise<unknown>,
  now = Date.now()
): Promise<SeatHold | null> => {
  const hold = parseSeatHold(await getCurrentSeatHold());
  return hold && !isSeatHoldExpired(hold, now) ? hold : null;
};

export const getSeatPickerHoldAction = (
  hold?: SeatHold | null,
  now = Date.now()
) => {
  if (!hold) {
    return "open_picker" as const;
  }

  return isSeatHoldExpired(hold, now)
    ? ("clear_hold_and_open_picker" as const)
    : ("reopen_checkout" as const);
};

export const unavailableSeatKeysFromError = (error: any) => {
  const unavailableSeats =
    error?.body?.detail?.unavailableSeats ||
    error?.detail?.unavailableSeats ||
    error?.body?.unavailableSeats ||
    error?.detail?.unavailableSeats;

  return Array.isArray(unavailableSeats)
    ? unavailableSeats.filter(
        (seatKey): seatKey is string => typeof seatKey === "string"
      )
    : [];
};

export const isSeatHoldConflict = (error: any) => error?.status === 409;
